import { EdgelessLegacySlotIdentifier } from '@blocksuite/affine-block-surface';
import {
  type AttachmentBlockModel,
  AttachmentBlockSchema,
  AttachmentBlockStyles,
} from '@blocksuite/affine-model';
import {
  EDGELESS_CANVAS_CARD_SHADOW,
  EMBED_CARD_HEIGHT,
  EMBED_CARD_WIDTH,
} from '@blocksuite/affine-shared/consts';
import { Bound } from '@blocksuite/global/gfx';
import { toGfxBlockComponent } from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import { styleMap } from 'lit/directives/style-map.js';

import { AttachmentBlockComponent } from './attachment-block.js';
import { HTML_ATTACHMENT_FOCUS_OUT_EVENT } from './embed.js';

const CARD_BOUND_TOLERANCE = 0.5;
const CARD_RATIO_TOLERANCE = 0.02;

function isNearlyEqual(left: number, right: number) {
  return Math.abs(left - right) <= CARD_BOUND_TOLERANCE;
}

export class AttachmentEdgelessBlockComponent extends toGfxBlockComponent(
  AttachmentBlockComponent
) {
  override blockDraggable = false;

  private _htmlAttachmentFocusOutTimer: ReturnType<typeof setTimeout> | null =
    null;

  private _isNormalizingCardBound = false;

  get slots() {
    return this.std.get(EdgelessLegacySlotIdentifier);
  }

  override onClick(_: MouseEvent) {
    return;
  }

  protected override get embedInteractive() {
    return this.selected$.value;
  }

  private readonly _onHtmlAttachmentFocusOut = () => {
    if (this._htmlAttachmentFocusOutTimer) {
      clearTimeout(this._htmlAttachmentFocusOutTimer);
    }

    this._htmlAttachmentFocusOutTimer = setTimeout(() => {
      this._htmlAttachmentFocusOutTimer = null;
      if (!this.isConnected) return;
      if (this.gfx.tool.dragging$.value) return;

      const selectedIds = this.gfx.selection.selectedIds;
      if (!selectedIds.includes(this.model.id)) return;

      this.gfx.selection.set({
        elements: selectedIds.filter(id => id !== this.model.id),
        editing: false,
      });
    }, 0);
  };

  override connectedCallback() {
    super.connectedCallback();
    this.style.borderRadius = '8px';
    this.style.boxShadow = EDGELESS_CANVAS_CARD_SHADOW;

    this.disposables.addFromEvent(
      this,
      HTML_ATTACHMENT_FOCUS_OUT_EVENT,
      this._onHtmlAttachmentFocusOut
    );
  }

  override disconnectedCallback() {
    if (this._htmlAttachmentFocusOutTimer) {
      clearTimeout(this._htmlAttachmentFocusOutTimer);
      this._htmlAttachmentFocusOutTimer = null;
    }

    super.disconnectedCallback();
  }

  private _isCompatibleCardBound(bound: Bound, width: number, height: number) {
    if (isNearlyEqual(bound.w, width) && isNearlyEqual(bound.h, height)) {
      return true;
    }
    if (bound.w <= 0 || bound.h <= 0 || width <= 0 || height <= 0) {
      return false;
    }

    const expectedRatio = width / height;
    const actualRatio = bound.w / bound.h;
    return (
      Math.abs(actualRatio - expectedRatio) / expectedRatio <=
      CARD_RATIO_TOLERANCE
    );
  }

  private _queueNormalizeCardBound() {
    if (this._isNormalizingCardBound) return;

    this._isNormalizingCardBound = true;
    queueMicrotask(() => {
      this._isNormalizingCardBound = false;
      if (!this.isConnected) return;
      if (this.model.props.embed$.value === true) return;

      const cardStyle =
        this.model.props.style$.value ?? AttachmentBlockStyles[1];
      const nextWidth = EMBED_CARD_WIDTH[cardStyle];
      const nextHeight = EMBED_CARD_HEIGHT[cardStyle];
      const bound = Bound.deserialize(this.model.xywh);
      if (
        isNearlyEqual(bound.w, nextWidth) &&
        isNearlyEqual(bound.h, nextHeight)
      )
        return;

      bound.w = nextWidth;
      bound.h = nextHeight;
      const serialized = bound.serialize();
      if (this.store.readonly) {
        this.model.transientXYWH = serialized;
      } else {
        this.model.xywh = serialized;
      }
      this._refreshSelectionBounds();
    });
  }

  private _refreshSelectionBounds() {
    if (!this.gfx.selection.has(this.model.id)) return;

    this.gfx.selection.set({
      elements: this.gfx.selection.selectedIds,
      editing: this.gfx.selection.editing,
    });
  }

  override renderGfxBlock() {
    const bound = this.model.elementBound;
    const isEmbed = this.model.props.embed$.value === true;
    const cardStyle = this.model.props.style$.value ?? AttachmentBlockStyles[1];
    const cardWidth = EMBED_CARD_WIDTH[cardStyle];
    const cardHeight = EMBED_CARD_HEIGHT[cardStyle];
    const useModelBound =
      isEmbed || this._isCompatibleCardBound(bound, cardWidth, cardHeight);
    const width = useModelBound ? bound.w : cardWidth;
    const height = useModelBound ? bound.h : cardHeight;

    if (!isEmbed && !useModelBound) {
      this._queueNormalizeCardBound();
    }

    this.containerStyleMap = styleMap({
      width: `${width}px`,
      height: `${height}px`,
      // Visible so the embedding badge popover is not clipped; the embed
      // content clips itself via .affine-attachment-embed-container.
      overflow: 'visible',
    });

    return this.renderPageContent();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-edgeless-attachment': AttachmentEdgelessBlockComponent;
  }
}

export const AttachmentBlockInteraction = GfxViewInteractionExtension(
  AttachmentBlockSchema.model.flavour,
  {
    selectionBorderRadius: 8,
    resizeConstraint: {
      lockRatio: true,
    },
    handleRotate: () => {
      return {
        beforeRotate: context => {
          context.set({
            rotatable: false,
          });
        },
      };
    },
    handleSelection: ({ gfx, model }) => {
      const attachmentModel = model as AttachmentBlockModel;

      return {
        onSelect(context) {
          const { multiSelect, selected } = context;
          const alreadySelected = gfx.selection.has(attachmentModel.id);
          const isInteractiveEmbed =
            attachmentModel.props.embed === true &&
            (attachmentModel.props.style === 'html' ||
              attachmentModel.props.style === 'video');

          if (
            isInteractiveEmbed &&
            !multiSelect &&
            selected &&
            (alreadySelected || gfx.selection.editing)
          ) {
            if (attachmentModel.isLocked()) return;

            if (alreadySelected && gfx.selection.editing) {
              return;
            }

            gfx.selection.set({
              elements: [attachmentModel.id],
              editing: true,
            });
            return;
          }

          context.default(context);
        },
      };
    },
  }
);
